import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { TechWorkStatus } from '../types/ticket';
import type { TechWorkStatus as TechWorkStatusType } from '../types/ticket';

interface ActiveJob {
  ticketId: string;
  ticketNumber: string;
}

interface UserStatusContextType {
  workStatus: TechWorkStatusType;
  setWorkStatus: (status: TechWorkStatusType) => void;
  workStartTime: Date | undefined;
  activeJob: ActiveJob | null;
  startJob: (ticketId: string, ticketNumber: string) => void;
  stopJob: () => void;
  signOut: () => void;
}

const UserStatusContext = createContext<UserStatusContextType | undefined>(undefined);

export function UserStatusProvider({ children }: { children: ReactNode }) {
  const [workStatus, setWorkStatusState] = useState<TechWorkStatusType>(
    () => (localStorage.getItem('userWorkStatus') as TechWorkStatusType) || TechWorkStatus.AVAILABLE
  );

  const [workStartTime, setWorkStartTime] = useState<Date | undefined>(() => {
    const saved = localStorage.getItem('workStartTime');
    return saved ? new Date(saved) : undefined;
  });

  const [activeJob, setActiveJob] = useState<ActiveJob | null>(() => {
    const saved = localStorage.getItem('activeJob');
    return saved ? JSON.parse(saved) : null;
  });

  // Sync to localStorage
  useEffect(() => { localStorage.setItem('userWorkStatus', workStatus); }, [workStatus]);

  useEffect(() => {
    workStartTime
      ? localStorage.setItem('workStartTime', workStartTime.toISOString())
      : localStorage.removeItem('workStartTime');
  }, [workStartTime]);

  useEffect(() => {
    activeJob
      ? localStorage.setItem('activeJob', JSON.stringify(activeJob))
      : localStorage.removeItem('activeJob');
  }, [activeJob]);

  const setWorkStatus = (status: TechWorkStatusType) => {
    setWorkStatusState(status);
    if (status !== TechWorkStatus.WORKING) {
      setActiveJob(null);
      setWorkStartTime(undefined);
    }
  };

  const startJob = (ticketId: string, ticketNumber: string) => {
    setActiveJob({ ticketId, ticketNumber });
    setWorkStatusState(TechWorkStatus.WORKING);
    setWorkStartTime(new Date());
  };

  const stopJob = () => {
    setActiveJob(null);
    setWorkStatusState(TechWorkStatus.AVAILABLE);
    setWorkStartTime(undefined);
  };

  const signOut = () => {
    setActiveJob(null);
    setWorkStatusState(TechWorkStatus.OFFLINE);
    setWorkStartTime(undefined);
    localStorage.removeItem('activeJob');
    localStorage.removeItem('workStartTime');
    localStorage.removeItem('userWorkStatus');
    // TODO: hook up real auth logout here
  };

  return (
    <UserStatusContext.Provider value={{
      workStatus, setWorkStatus, workStartTime, activeJob, startJob, stopJob, signOut
    }}>
      {children}
    </UserStatusContext.Provider>
  );
}

export function useUserStatus() {
  const ctx = useContext(UserStatusContext);
  if (!ctx) throw new Error('useUserStatus must be used within UserStatusProvider');
  return ctx;
}
